import {
	Box,
	Button,
	Inline,
	Icons,
	Text,
	Stack,
	Spinner,
	Select,
	AsyncSelect,
	AsyncSelectProps,
	Divider,
	Alert,
} from "@sembark-travel/ui-base"
import { ITrip } from "./store"
import {
	Form,
	SelectField,
	SubmissionError,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { useXHR, XHRInstance } from "@sembark-travel/xhr"
import { useNavigate } from "../router"
import useSWR from "swr"
import * as Validator from "yup"

const tripPurposeOptions = [
	{ value: "leisure_holiday", name: "Leisure Holiday" },
	{ value: "honeymoon", name: "Honeymoon" },
	{ value: "anniversary", name: "Anniversary" },
	{ value: "family_holiday", name: "Family Holiday" },
	{ value: "friends_getaway", name: "Friends Getaway" },
	{ value: "adventure_expedition", name: "Adventure Expedition" },
	{ value: "wellness_retreat", name: "Wellness Retreat" },
	{ value: "wildlife_safari", name: "Wildlife Safari" },
	{ value: "cultural_heritage", name: "Cultural Heritage" },
	{ value: "pilgrimage_religious", name: "Pilgrimage / Religious" },
	{ value: "cruise_holiday", name: "Cruise Holiday" },
	{ value: "corporate_travel", name: "Corporate Travel" },
	{ value: "bleisure", name: "Bleisure (Business + Leisure)" },
	{ value: "educational_study_tour", name: "Educational / Study Tour" },
]

const tourBasisOptions = [
	{ value: "private_fit", name: "Private FIT" },
	{ value: "seat_in_coach", name: "Seat in Coach (SIC)" },
	{ value: "group_inclusive_tour", name: "Group Inclusive Tour (GIT)" },
]

const budgetTierOptions = [
	{ value: "luxury", name: "Luxury" },
	{ value: "mid_range", name: "Mid Range" },
	{ value: "budget", name: "Budget" },
]
function XHR(xhr: XHRInstance) {
	return {
		locationConfig: (params: {
			q: string
			total_nights: ITrip["nights"]
			destination_id: number[]
		}): Promise<{ id: number; location_config: string }[]> => {
			return xhr
				.get("/itinerary-builder/location-configs", { params })
				.then((resp) => resp.data.data)
		},
	}
}

export function AiItineraryBuilder({ trip }: { trip: ITrip }) {
	return (
		<Box backgroundColor="default" padding="4" rounded="md">
			<Stack gap="4">
				<Inline gap="4" alignItems="center">
					<Box
						padding="3"
						borderWidth={"1"}
						borderColor={"primary"}
						rounded="full"
						backgroundColor="primary_emphasis"
					>
						<Icons.MagicStick color="on_emphasis" size={"6"} />
					</Box>
					<Stack>
						<Text fontSize="lg" fontWeight="semibold">
							AI Itinerary Builder
						</Text>
						<Text color="muted" fontSize="sm">
							Describe your travel preferences, and let AI design an itinerary
							that balances destinations, hotels, experiences, travel time, and
							budget.
							<br />
							<Icons.LightBulb opacity="70" /> Tip: AI considers your trip
							purpose, package type, budget, and travel style to create smarter
							recommendations.
						</Text>
					</Stack>
				</Inline>
				<ItineraryBuilderForm trip={trip} />
			</Stack>
		</Box>
	)
}

function ItineraryBuilderForm({ trip }: { trip: ITrip }) {
	const navigate = useNavigate()
	const xhr = useXHR()
	const { data, error } = useSWR(trip.id.toString(), () =>
		xhr.get("/itinerary-builder/credit-stats").then((res) => res.data)
	)
	const isFetching = !data && !error
	if (isFetching) {
		return (
			<Inline justifyContent={"center"} gap={"2"} alignItems={"center"}>
				<Spinner /> Loading credit...
			</Inline>
		)
	}

	return (
		<Form<{
			name: string
			trip_purpose?: (typeof tripPurposeOptions)[number]
			tour_basis?: (typeof tourBasisOptions)[number]
			budget_tier?: typeof budgetTierOptions
			location_config?: { id: number; location_config: string }
		}>
			onSubmit={withServerErrors(async (values) => {
				const response = await xhr.get(`/itinerary-builder/${trip.id}`, {
					params: {
						trip_purpose: values?.trip_purpose?.value,
						tour_basis: values?.tour_basis?.value,
						budget_tier: values?.budget_tier?.map((b) => b.value),
						location_config: values?.location_config?.location_config,
					},
				})
				const { generation_id, offset } = response.data.data
				navigate(
					{
						pathname: "/trips/:tripId/ai-itinerary-builder/new-quote",
						search: `?generationId=${generation_id || ""}&offset=${offset || ""}`,
					},
					{
						params: {
							tripId: trip.id.toString(),
						},
					}
				)
			})}
			subscription={{ submitting: true }}
			validate={validateFormValues(
				Validator.object().shape({
					budget_tier: Validator.array()
						.required("Please provide atleast one Budget Tier")
						.min(1, "Please provide atleast one Budget Tier"),
					location_config: Validator.mixed().required(
						"Please select a Location Config"
					),
				})
			)}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<Stack gap="4">
						<Stack>
							<Inline flexWrap={"wrap"} gap="4" alignItems="start">
								<SelectField
									select={Select}
									options={tripPurposeOptions}
									searchable
									placeholder="Select Trip Purpose..."
									creatable
									name="trip_purpose"
									label="Trip Purpose"
									secondaryLabel="Optional"
								/>
								<SelectField
									select={Select}
									options={tourBasisOptions}
									placeholder="Select Tour Basis..."
									name="tour_basis"
									label="Tour Basis"
									secondaryLabel="Optional"
								/>
								<SelectField
									select={Select}
									options={budgetTierOptions}
									label="Budget Tier"
									placeholder="Select Budget Tier..."
									name="budget_tier"
									multiple
								/>
								<SelectField
									select={SelectLocationConfig}
									label="Location Config"
									name="location_config"
									fetchOnMount
									destinations={trip.destinations}
									total_nights={trip.nights}
									labelKey="location_config"
									creatable
								/>
							</Inline>
							<Divider />
							<Stack gap={"2"}>
								<SubmissionError />
								<Inline gap="2" alignItems="center">
									<Button type="submit" level="primary" disabled={submitting}>
										{submitting ? <Spinner /> : "Generate Itinerary"}
									</Button>
								</Inline>
							</Stack>
						</Stack>

						{data &&
						data?.balance < 200 &&
						data?.required_balance < data?.balance ? (
							<Alert hideIcon status="warning" title="Low Balance">
								You have {data.balance} credits remaining. Please top up your
								account to continue using the AI Itinerary Builder.
							</Alert>
						) : null}
					</Stack>
				</form>
			)}
		</Form>
	)
}

function SelectLocationConfig(
	props: Omit<
		AsyncSelectProps & {
			destinations: ITrip["destinations"]
			total_nights: ITrip["nights"]
		},
		"fetch"
	>
) {
	const xhr = useXHR()
	return (
		<AsyncSelect
			fetch={(q) =>
				XHR(xhr)
					.locationConfig({
						q,
						total_nights: props.total_nights,
						destination_id: props.destinations.map((d) => d.id) || [],
					})
					.then((resp) => resp)
			}
			{...props}
		/>
	)
}
